import * as ngCommonHttp from '@angular/common/http';

import { Modifier, ModifiersResponse } from '@interfaces/modifier/1.0';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap, toArray } from 'rxjs/operators';

import { AbstractModifierService } from '@abstract-services/modifier/1.0/modifier.abstract-service';
import { Injectable } from '@angular/core';
import { CatalogEntriesAPI, ModifierAPI } from '@constants/api';
import { CatalogItemType } from '@constants/catalogItem/1.0';
import { CATALOG_PAGE_SIZE_OTHER_CATALOG_ITEMS } from '@constants/pagination/1.0';
import { CatalogItemsService } from '@services/catalog-items/1.0/catalog-items.service';
import { PaginationService } from '@services/pagination/1.0/pagination.service';

@Injectable({
  providedIn: 'root',
})
export class ModifierService extends AbstractModifierService {
  modifiersGetApiUrl;
  modifierPutApiUrl;
  patchUrl;
  itemType = CatalogItemType.MODIFIER;

  // save the list of modifiers after fetching but keep track of which brand this is for so we can reset it
  cachedBrandId: string;
  fetchReplay: Observable<Modifier[]>;

  private isCopying = new BehaviorSubject<boolean>(false);
  isCopying$ = this.isCopying.asObservable();
  copiedModifier: Modifier;

  constructor(
    private http: ngCommonHttp.HttpClient,
    private paginationService: PaginationService,
    private catalogItemsService: CatalogItemsService,
  ) {
    super();
    this.modifiersGetApiUrl = ModifierAPI.GET;
    this.modifierPutApiUrl = ModifierAPI.PUT;
    this.patchUrl = CatalogEntriesAPI.PATCH;
  }

  create(modifier: Partial<Modifier>, brandId: string): Observable<Modifier> {
    const BODY = Object.assign({}, modifier);
    if (!BODY.instructionIds) {
      BODY.instructionIds = [];
    }
    const url = this.modifiersGetApiUrl.replace(':tenantId', brandId);
    return this.http.post(url, BODY).pipe(
      map((response: Modifier) => {
        return response;
      }),
      tap((m: Modifier) => {
        if (this.cachedBrandId === brandId && this.catalogItemsService.getCache(this.itemType)) {
          this.catalogItemsService.addItemCache(m, this.itemType);
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getModifiers(brandId: string, includeDeleted: boolean = false): Observable<Modifier[]> {
    // reset cache if this is a different brand ID
    if (this.cachedBrandId !== brandId) {
      this.catalogItemsService.resetMasterCache();
      this.cachedBrandId = brandId;
      this.fetchReplay = undefined;
    }

    if (!this.catalogItemsService.getCache(this.itemType)) {
      this.catalogItemsService.updateCache(this.itemType);

      let url = this.modifiersGetApiUrl.replace(':tenantId', brandId);
      url += `?includeDeleted=${includeDeleted}`;

      return (this.fetchReplay = this.paginationService
        .fetchAllPages<ModifiersResponse>(url, { pageSize: CATALOG_PAGE_SIZE_OTHER_CATALOG_ITEMS })
        .pipe(
          toArray(), // collect all pages into an array
          map((pages: ModifiersResponse[]) =>
            // flatten all pages' modifiers into one array
            pages
              .filter((r) => r && r._embedded && r._embedded.modifiers)
              .reduce<Modifier[]>((all, r) => all.concat(r._embedded.modifiers), []),
          ),
          tap((allModifiers: Modifier[]) => {
            // update cache once with the full list
            this.catalogItemsService.updateCache(this.itemType, allModifiers);
          }),
          shareReplay(1),
          catchError((err: ngCommonHttp.HttpErrorResponse) => {
            this.catalogItemsService.clearCache(this.itemType);
            return throwError(err);
          }),
        ));
    } else if (this.catalogItemsService.getCache(this.itemType).length === 0) {
      return this.fetchReplay;
    } else {
      return of(this.catalogItemsService.getCache(this.itemType));
    }
  }

  save(catalogEntryId: string, modifier: Modifier, remove: string[] = []): Observable<Modifier> {
    const url = `${this.patchUrl}/${catalogEntryId}`;
    const payload = {
      set: {
        ...modifier,
      },
      remove,
    };
    return this.http.patch(url, payload).pipe(
      tap((response: any) => {
        let updatedModifier = modifier;
        if (response && response.nutritionTexts) {
          updatedModifier = { ...modifier, nutritionTexts: response.nutritionTexts } as any;
        }
        this.catalogItemsService.updateItemCache(updatedModifier, this.itemType);
      }),
      map((response: any) => {
        if (response && response.nutritionTexts) {
          return { ...modifier, nutritionTexts: response.nutritionTexts } as any;
        }
        return modifier;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  setCopyingState(isCopying: boolean) {
    this.isCopying.next(isCopying);
  }

  /**
   * Get the current copying state
   * @returns boolean indicating if a modifier is currently being copied
   */
  getCopyingState(): boolean {
    return this.isCopying.value;
  }
}
